import type { GetProjectQuery } from '@/survey-graphql';
import { QuestionType, OptionRenderType } from '@/survey-graphql';

import SurveyAiFormsAiSectionImageChoiceQuestion from './survey-ai-forms-ai-section-image-choice-question';
import SurveyAiFormsAiSectionMatrixQuestion from './survey-ai-forms-ai-section-matrix-question';
import SurveyAiFormsAiSectionOpenEndedQuestion from './survey-ai-forms-ai-section-open-ended-question';
import SurveyAiFormsAiSectionRankingQuestion from './survey-ai-forms-ai-section-ranking-question';
import SurveyAiFormsAiSectionRatingQuestion from './survey-ai-forms-ai-section-rating-question';
import SurveyAiFormsAiSectionSelectQuestion from './survey-ai-forms-ai-section-select-question';
import {
  getQuestionValueAsString,
  getQuestionValueAsArray,
  isMatrixQuestion,
} from './survey-ai-forms.helpers';
import type { FormAnswerValue, ResponseMode } from './survey-ai-forms.types';

type ProjectQuestion = NonNullable<
  GetProjectQuery['project']
>['questions'][number];

interface Props {
  question: ProjectQuestion;
  questionValue: FormAnswerValue | undefined;
  transcriptValue: string;
  openEndedDisplayValue?: string;
  onQuestionValueChange: (value: FormAnswerValue) => void;
  onOpenEndedChange?: (value: string) => void;
  onTranscriptChange: (value: string) => void;
  onSubmit: () => void;
  onNext?: () => void;
  hasValue: boolean;
  responseMode: ResponseMode;
  loading?: boolean;
  isMobile?: boolean;
  isUserVoiceEnabled?: boolean;
}

export default function SurveyAiFormsAiSectionQuestionRenderer({
  question,
  questionValue,
  transcriptValue,
  openEndedDisplayValue = '',
  onQuestionValueChange,
  onOpenEndedChange,
  onTranscriptChange,
  onSubmit,
  onNext,
  hasValue,
  responseMode,
  loading = false,
  isMobile = false,
  isUserVoiceEnabled = false,
}: Props) {
  // Convert question value to appropriate type for each question component
  const valueAsString = getQuestionValueAsString(questionValue);
  const valueAsArray = getQuestionValueAsArray(questionValue);
  switch (question.type) {
    case QuestionType.OpenEnded:
      return (
        <SurveyAiFormsAiSectionOpenEndedQuestion
          question={question}
          value={openEndedDisplayValue}
          onChange={onOpenEndedChange || onTranscriptChange}
          onSubmit={onSubmit}
          onNext={onNext}
          responseMode={responseMode}
          loading={loading}
          isMobile={isMobile}
          isUserVoiceEnabled={isUserVoiceEnabled}
        />
      );

    case QuestionType.Rating:
      if (isMatrixQuestion(question)) {
        return (
          <SurveyAiFormsAiSectionMatrixQuestion
            question={question}
            value={questionValue}
            transcriptValue={transcriptValue}
            onChange={onQuestionValueChange}
            onTranscriptChange={onTranscriptChange}
            onSubmit={onSubmit}
            onNext={onNext}
            hasValue={hasValue}
            responseMode={responseMode}
            loading={loading}
            isMobile={isMobile}
            isUserVoiceEnabled={isUserVoiceEnabled}
          />
        );
      }
      return (
        <SurveyAiFormsAiSectionRatingQuestion
          question={question}
          value={valueAsString}
          transcriptValue={transcriptValue}
          onChange={onQuestionValueChange}
          onTranscriptChange={onTranscriptChange}
          onSubmit={onSubmit}
          onNext={onNext}
          hasValue={hasValue}
          responseMode={responseMode}
          loading={loading}
          isMobile={isMobile}
          isUserVoiceEnabled={isUserVoiceEnabled}
        />
      );

    case QuestionType.Ranking:
      return (
        <SurveyAiFormsAiSectionRankingQuestion
          question={question}
          value={valueAsArray}
          transcriptValue={transcriptValue}
          onChange={onQuestionValueChange}
          onTranscriptChange={onTranscriptChange}
          onSubmit={onSubmit}
          onNext={onNext}
          hasValue={hasValue}
          responseMode={responseMode}
          loading={loading}
          isMobile={isMobile}
          isUserVoiceEnabled={isUserVoiceEnabled}
        />
      );

    case QuestionType.MultipleChoice: {
      // Image choice variant
      if (question.optionRenderType === OptionRenderType.Media) {
        const isMultiSelect = (question.maxSelections ?? 1) > 1;
        return (
          <SurveyAiFormsAiSectionImageChoiceQuestion
            question={question}
            value={isMultiSelect ? valueAsArray : valueAsString}
            transcriptValue={transcriptValue}
            onChange={onQuestionValueChange}
            onTranscriptChange={onTranscriptChange}
            onSubmit={onSubmit}
            onNext={onNext}
            hasValue={hasValue}
            responseMode={responseMode}
            loading={loading}
            isMobile={isMobile}
            isUserVoiceEnabled={isUserVoiceEnabled}
          />
        );
      }
      // Regular multiple choice (single or multi-select)
      // Check if it's multi-select based on maxSelections
      const isMultiSelect = (question.maxSelections ?? 1) > 1;
      return (
        <SurveyAiFormsAiSectionSelectQuestion
          question={question}
          value={isMultiSelect ? valueAsArray : valueAsString}
          transcriptValue={transcriptValue}
          onChange={onQuestionValueChange}
          onTranscriptChange={onTranscriptChange}
          onSubmit={onSubmit}
          onNext={onNext}
          hasValue={hasValue}
          responseMode={responseMode}
          loading={loading}
          isMobile={isMobile}
          isUserVoiceEnabled={isUserVoiceEnabled}
        />
      );
    }

    default:
      return null;
  }
}
